import AccountModal from "@shared/modules/login/components/AccountModal/AccountModal.web";
import { AccountTabType } from "@shared/modules/login/components/AccountModal/AccountModal.props";
import { AnalyticsContextProvider } from "@shared/contexts/AnalyticsContext/useAnalyticsContext";
import { LegacyDashboardProvider } from "@shared/contexts/LegacyDashboardContext/useLegacyDashboard";
import { DashboardAuthProvider } from "@app-contexts/dashboard/contexts/DashboardAuthProvider";
import { FACEBOOK_TRACKING_PIXEL, APP_ENV } from "@shared/env";
import { useUserSessionAuthState } from "@shared/modules/auth/contexts/useUserSessionAuthState";
import useCsrfTokens from "@shared/modules/auth/hooks/useCsrfTokens";
import { useContextualRouting } from "next-use-contextual-routing";
import { useRouter } from "next/router";
import { PropsWithChildren, useEffect } from "react";
import facebookpixel from "@shared/lib/analytics/facebookpixel";
import NavBar from "@shared/components/application/navigation/NavBar/NavBar.web";
import { BasketServiceProvider } from "@shared/modules/basket/context/BasketServiceContext";
import { useVenueBasket } from "@app-contexts/dashboard/features/box-office/basket/services/useVenueBasket";
import Flex from "@shared/components/ui-kit/layout/Flex/Flex.web";
import Box from "@shared/components/ui-kit/layout/Box/Box.web";
import Footer from "../../navigation/footer/Footer/Footer.web";

/**
 * Sets footer to bottom of page even
 * without sufficient page content
 */
const FlexContainer = ({ children, ...props }: PropsWithChildren) => (
  <Flex direction="column" minHeight="var(--100dvh)" {...props}>
    {children}
  </Flex>
);

const Template = ({ children }: PropsWithChildren) => {
  const router = useRouter();
  const { returnHref } = useContextualRouting();
  const { isLoggedIn, userProfileData } = useUserSessionAuthState();

  useCsrfTokens({
    enabled: APP_ENV === "preview" && !!isLoggedIn,
  });
  useEffect(() => {
    if (userProfileData) {
      facebookpixel.init(FACEBOOK_TRACKING_PIXEL, userProfileData);
    }
  }, [userProfileData]);

  const AccountModalComponent = (
    <>
      {router.query?.accountModal && (
        <AccountModal
          isOpen={!!router.query?.accountModal}
          startingTab={router.query?.accountModal as AccountTabType}
          onSuccess={() => {
            router.push(returnHref, undefined, { shallow: true });
          }}
          onClose={() => {
            router.push(returnHref, undefined, { shallow: true });
          }}
        />
      )}
    </>
  );

  if (router.pathname.startsWith("/account/spotify")) {
    return <>{children}</>;
  }

  if (router.pathname.startsWith("/account/")) {
    return (
      <FlexContainer>
        <NavBar />
        {AccountModalComponent}
        <Box flex="1">{children}</Box>
        <Footer />
      </FlexContainer>
    );
  }

  // Legacy AngularJS routes (/e/, /m/) and new branded routes (/event/, /pass/)
  if (
    router.pathname.startsWith("/e/") ||
    router.pathname.startsWith("/m/") ||
    router.pathname.startsWith("/event/") ||
    router.pathname.startsWith("/pass/")
  ) {
    return (
      <FlexContainer>
        {AccountModalComponent}
        {children}
      </FlexContainer>
    );
  }

  if (router.pathname.startsWith("/accounts/")) {
    return <>{children}</>;
  }

  if (router.pathname.startsWith("/manage")) {
    return (
      <BasketServiceProvider basketService={useVenueBasket}>
        <AnalyticsContextProvider enableAnalytics={false}>
          <DashboardAuthProvider>
            <LegacyDashboardProvider>
              <Box bg="bg.subtle" minH="100vh">
                {children}
              </Box>
            </LegacyDashboardProvider>
          </DashboardAuthProvider>
        </AnalyticsContextProvider>
      </BasketServiceProvider>
    );
  }

  return (
    <FlexContainer>
      {AccountModalComponent}
      {children}
    </FlexContainer>
  );
};

export default Template;
